"use client";

/**
 * OrphanScreen — shown when an authenticated user has zero org memberships
 * after a successful (non-error) fetch of GET /api/organizations/me.
 *
 * This happens to invite-first users who were removed from their only tenant,
 * or whose tenant became unavailable (suspended/deleted). D4 policy: never
 * auto-create a personal workspace or silently redirect — always show WHY
 * and require an explicit opt-in click.
 *
 * On mount the component fetches GET /api/organizations/me/workspace-status to
 * learn the reason. If the endpoint says has_workspace=true (e.g. the user
 * was re-added between the two calls), it renders the "retry" fallback so the
 * caller can re-try the memberships fetch.
 *
 * The primary CTA ("Continue with a free personal account") calls
 * POST /api/organizations/me/personal (idempotent) and routes to
 * /o/{org_slug}/dashboard on success.
 */

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { apiFetch } from "@/lib/api";
import type { WorkspaceStatus, PersonalOrgResult } from "@/lib/types";
import FullScreenSpinner from "@/components/ui/FullScreenSpinner";

interface OrphanScreenProps {
  /** Called when the user clicks "Sign out". */
  onSignOut: () => void;
  /**
   * Rendered when workspace-status indicates the user actually has a workspace
   * (has_workspace === true). Caller should surface a retry / connection-error UI.
   */
  fallback: React.ReactNode;
}

export default function OrphanScreen({ onSignOut, fallback }: OrphanScreenProps) {
  const router = useRouter();
  const [status, setStatus] = useState<WorkspaceStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<WorkspaceStatus>("/organizations/me/workspace-status")
      .then(setStatus)
      .catch(() =>
        // Network error / unexpected — treat as orphan with no specific reason
        setStatus({ has_workspace: false, reason: null, org_name: null })
      )
      .finally(() => setStatusLoading(false));
  }, []);

  if (statusLoading) return <FullScreenSpinner />;

  // User still has a workspace (race — added between calls, or stale memberships)
  if (status?.has_workspace) return <>{fallback}</>;

  // ── Derive headline copy ──────────────────────────────────────────────────────
  const orgName = status?.org_name ?? null;
  let headline: React.ReactNode;
  let subline: string;

  if (status?.reason === "removed") {
    headline = orgName ? (
      <>
        You&apos;ve been removed from{" "}
        <span className="font-semibold text-ink">{orgName}</span>
      </>
    ) : (
      "You've been removed from your workspace"
    );
    subline =
      "Your account is still active. You can continue with a free personal workspace — you own it, and it never expires.";
  } else if (status?.reason === "org_unavailable") {
    headline = orgName ? (
      <>
        <span className="font-semibold text-ink">{orgName}</span> is no longer
        available
      </>
    ) : (
      "Your workspace is no longer available"
    );
    subline =
      "This organisation has been suspended or removed. Your account is intact — start a free personal workspace to keep going.";
  } else {
    headline = "Your workspace is no longer available";
    subline =
      "Your account is still active. You can continue with a free personal workspace — you own it, and it never expires.";
  }

  // ── CTA handler ───────────────────────────────────────────────────────────────
  const handleContinue = async () => {
    setCreating(true);
    setCreateError(null);
    try {
      const result = await apiFetch<PersonalOrgResult>("/organizations/me/personal", {
        method: "POST",
      });
      // Hard navigate so the auth context fully reloads with the new membership.
      window.location.assign(`/o/${result.org_slug}/dashboard`);
    } catch {
      setCreateError("Something went wrong — please try again.");
      setCreating(false);
    }
  };

  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-paper px-4">
      <div className="flex w-full max-w-sm flex-col items-center gap-8 text-center">

        {/* Icon — a departing door / workspace symbol */}
        <div className="relative">
          {/* Ambient amber glow */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(212,160,18,0.14) 0%, transparent 70%)",
              transform: "scale(2.4)",
            }}
            aria-hidden
          />
          <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-brand-200 bg-brand-50">
            {/* Door-exit icon */}
            <svg
              viewBox="0 0 24 24"
              fill="none"
              className="h-8 w-8 text-brand-600"
              aria-hidden
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              {/* Door frame */}
              <path
                d="M13 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7"
                stroke="currentColor"
              />
              {/* Arrow pointing out (right) */}
              <path d="M17 8l4 4-4 4" stroke="currentColor" />
              <line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" />
            </svg>
          </div>
        </div>

        {/* Copy */}
        <div className="flex flex-col gap-2">
          <h1 className="font-display text-xl font-semibold tracking-tight text-ink-light">
            {headline}
          </h1>
          <p className="text-sm leading-relaxed text-ink-lighter">{subline}</p>
        </div>

        {/* Primary CTA */}
        <div className="flex w-full flex-col gap-3">
          <button
            onClick={handleContinue}
            disabled={creating}
            className="group relative inline-flex w-full items-center justify-center gap-2 rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-medium text-white transition-all duration-150 hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-paper active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creating ? (
              <>
                <svg
                  className="h-4 w-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeOpacity="0.25"
                    strokeWidth="3"
                  />
                  <path
                    d="M4 12a8 8 0 0 1 8-8"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
                Setting up your workspace…
              </>
            ) : (
              <>
                {/* House / workspace icon */}
                <svg
                  viewBox="0 0 16 16"
                  fill="none"
                  className="h-3.5 w-3.5"
                  aria-hidden
                >
                  <path
                    d="M2 6.5L8 2l6 4.5V14a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6.5z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 15V9h4v6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                  />
                </svg>
                Continue with a free personal account
              </>
            )}
          </button>

          {/* Inline error (POST failure) */}
          {createError && (
            <p
              role="alert"
              className="rounded-md bg-red-50 px-3 py-2 text-xs font-medium text-red-700"
            >
              {createError}
            </p>
          )}

          {/* Sign out link */}
          <button
            onClick={onSignOut}
            className="text-xs text-ink-lighter transition-colors duration-150 hover:text-ink"
          >
            ← Sign out
          </button>
        </div>

        {/* Reassurance footnote */}
        <p className="text-xs text-ink-lighter">
          Free accounts include 10 envelopes per month — no credit card required.
        </p>
      </div>
    </div>
  );
}
